<template>
  <div>
    <div class="cont" :class="creative !== null ? 'blurred' : ''">
      <header class="header">
        <nav-bar>
          <div slot="left" class="nav-item">
            <a href="https://www.adikteev.com/">
              <img src="../assets/logo.png" alt="MotionLead logo">
            </a>
          </div>
          <div slot="right" class="nav-item">
            <h2 class="subtitle contact" @click="showContact = true">Contact Us</h2>
          </div>
        </nav-bar>
      </header>

      <div class="filters">
        <div id="filters"></div>
        <div v-if="hasFilters" @click="clearFilters" class="delete-filters">
          Delete filters
        </div>
      </div>

      <div v-if="ready" class="item-list">
        <item v-for="(creative, index) in filteredCreatives" :item="creative" :delay="index * 50" :key="creative.key" @delete="deleteItem"></item>
      </div>
    </div>

    <overlay></overlay>
    <contact v-if="showContact" @dismiss="showContact = false"></contact>
  </div>

</template>

<script>
import NavBar from '@/components/NavBar';
import Item from '@/components/Item';
import Overlay from '@/components/Overlay';
import Contact from '@/components/Contact';

export default {
  name: 'ItemList',
  components: {
    NavBar,
    Item,
    Overlay,
    Contact,
  },
  data() {
    return {
      creatives: {},
      filteredCreatives: [],
      platforms: ['In-App', 'Mobile-Web', ...(window.innerWidth <= 768 ? [] : ['Desktop'])],
      formats: [],
      categories: [],
      advertisers: [],
      selectize: null,
      ready: false,
      showContact: false,
      hasFilters: false,
    };
  },
  computed: {
    creative() {
      return EventBus.creative;
    },
  },
  mounted() {
    const creativesPromise = firebase.database().ref('/creatives').once('value')
      .then((snapshot) => {
        const creatives = snapshot.val();
        if (!creatives) { return; }
        this.creatives = creatives;
        this.filteredCreatives = Object.keys(creatives)
          .map(key => ({ key, ...this.creatives[key] }))
          .filter(creative => this.platforms.includes(creative.platform))
          .reverse();
      });

    const formatsPromise = firebase.database().ref('/formats').once('value')
      .then((snapshot) => {
        const formats = snapshot.val();
        if (!formats) { return; }
        this.formats = Object.keys(formats).map(key => formats[key])
          .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
      });

    const categoriesPromise = firebase.database().ref('/iab-categories').once('value')
      .then((snapshot) => {
        const categories = snapshot.val();
        if (!categories) { return; }
        this.categories = Object.keys(categories).map(key => categories[key]);
      });

    const advertisersPromise = firebase.database().ref('/advertisers').once('value')
      .then((snapshot) => {
        const advertisers = snapshot.val();
        if (!advertisers) { return; }
        this.advertisers = Object.keys(advertisers).map(key => advertisers[key])
          .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
      });

    Promise.all([creativesPromise, formatsPromise, categoriesPromise, advertisersPromise])
      .then(() => {
        const options = [
          // Platforms
          ...this.platforms.map(platform => ({ id: platform, type: 'platform', text: platform }))
            .map((platform) => {
              const pl = platform;
              if (pl.id === 'In-App' || pl.id === 'Mobile-Web') {
                pl.id = 'Mobile';
                pl.text = 'Mobile';
              }
              return pl;
            }),
          // Formats
          ...this.formats.map(format => ({ id: format, type: 'format', text: format })),
          // Categories
          ...this.categories.map(category => ({ id: category, type: 'category', text: category })),
          // Advertisers
          ...this.advertisers.map(advertiser => ({ id: advertiser, type: 'advertiser', text: advertiser })),
        ];
        const selectize = $('#filters').selectize({
          options,
          optgroups: [
            { id: 'platform', text: 'Platform' },
            { id: 'format', text: 'Format' },
            { id: 'category', text: 'Category' },
            { id: 'advertiser', text: 'Advertiser' },
          ],
          labelField: 'text',
          valueField: 'id',
          optgroupField: 'type',
          optgroupLabelField: 'text',
          optgroupValueField: 'id',
          optgroupOrder: ['platform'],
          searchField: ['text'],
          placeholder: 'Filter creatives',
          plugins: ['optgroup_columns', 'remove_button'],
        })[0].selectize;

        // Set in data
        this.selectize = selectize;

        // Handle changes
        selectize.on('change', () => {
          const items = selectize.items.slice().sort();

          // Fix items for In-App & Mobile-Web
          if (items.includes('Mobile')) {
            items.splice(items.indexOf('Mobile'), 1);
            items.push('In-App', 'Mobile-Web');
          }

          // Has filters
          this.hasFilters = items.length > 0;

          // Update query string
          const query = Object.assign({}, this.$route.query);
          delete query.q;
          this.$router.replace({ query: Object.assign({ q: items }, query) });

          // Filter creatives
          this.filteredCreatives = Object.keys(this.creatives)
            .map(key => ({ key, ...this.creatives[key] }))
            .filter((creative) => {
              // Platform
              const noPlatform = !items.some(e => this.platforms.includes(e));
              const keepPlatform = items.includes(creative.platform) || noPlatform;

              // Format
              const noFormat = !items.some(e => this.formats.includes(e));
              const keepFormat = items.includes(creative.format) || noFormat;

              // Category
              const noCategory = !items.some(e => this.categories.includes(e));
              const keepCategory = items.includes(creative.category) || noCategory;

              // Advertiser
              const noAdvertiser = !items.some(e => this.advertisers.includes(e));
              const keepAdvertiser = items.includes(creative.advertiser) || noAdvertiser;

              return keepPlatform && keepFormat && keepCategory && keepAdvertiser;
            })
            .reverse();
        });

        // Preselect filters from query-string
        let selected = this.$route.query.q;
        if (selected) {
          if (!Array.isArray(selected)) {
            selected = [selected];
          }

          // Fix selected for In-App & Mobile-Web
          if (selected.includes('In-App') || selected.includes('Mobile-Web')) {
            selected.splice(selected.indexOf('In-App'), 1);
            selected.splice(selected.indexOf('Mobile-Web'), 1);
            selected.push('Mobile');
          }
          selected.forEach(option => selectize.addItem(option));
        }

        // Preselect creative from query-string
        const key = this.$route.query.id;
        if (key) {
          EventBus.creative = this.creatives[key];
        }

        this.ready = true;
      });
  },
  methods: {
    deleteItem(item) {
      firebase.database().ref(`creatives/${item.key}`).remove();
      this.filteredCreatives = this.filteredCreatives.filter(creative =>
        creative.key !== item.key);
    },
    clearFilters() {
      this.selectize.clear();
    },
  },
};
</script>

<style lang="scss" scoped>
  .item-list {
    display: flex;
    flex-wrap: wrap;
  }

  .cont {
    transition: filter .5s ease;
  }

  .blurred {
    filter: blur(8px);
  }

  .grow {
    flex-grow: 1;
  }

  .contact {
    color: white;
    font-weight: 400;
  }

  .contact:hover {
    cursor: pointer;
  }

  .filters {
    display: flex;
    background-color: #1F2224;
  }

  .delete-filters {
    color: #FFF;
    padding: 8px 8px;
    line-height: 18px;
    cursor: pointer;
  }

  .delete-filters:after {
    content: " \f057";
    font-family: FontAwesome;
  }

</style>

<style lang="css">
  .selectize-input:before {
    color: #FFF;
    content: "\f002 ";
    font-family: FontAwesome;
  }

  .selectize-control {
    flex-grow: 1;
  }

  .selectize-input > input::-moz-placeholder {
    color: #FFF;
  }

  .selectize-input > input::-webkit-input-placeholder {
    color: #FFF;
  }
</style>



// WEBPACK FOOTER //
// ItemList.vue?4e0d1862